import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import { Typography } from "@allaria-tech/matter/Typography";

import PartnerConsentBanner from "./PartnerConsentBanner";
import { asCuit } from "../../utils";
import { ConsentCopy } from "../../i18n/partnerConsent";
import { PartnerConsentFlow } from "../../utils/partnerConsent";

import styles from "./PartnerConsentPage.module.scss";

interface Props {
  flow: PartnerConsentFlow;
  copy: ConsentCopy;
  cuit: string;
  firstName: string;
  lastName: string;
  /** `numero` of the one picked on the welcome. */
  comitente: string;
  /** Drives the heading scale; see `PartnerConsentPage` for the single source. */
  isMobile: boolean;
  isSubmitting: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

/**
 * Linking's last stop: the data being linked, checked before the link is
 * burned. The only screen the opening variant never sees. Built from the same
 * blocks as the onboarding's own preconfirmation.
 */
const PartnerConsentPreconfirmation = ({
  flow,
  copy,
  cuit,
  firstName,
  lastName,
  comitente,
  isMobile,
  isSubmitting,
  onBack,
  onConfirm,
}: Props) => {
  const {
    screen,
    content,
    contentWide,
    preconfirmHeader,
    infoCard,
    infoGrid,
    infoField,
    actionsBar,
  } = styles;

  const { preconfirmation } = copy;

  // A dash rather than a collapsed row, which would shift the layout.
  const field = (label: string, value?: string) => (
    <div className={infoField}>
      <Typography type="HEADING" size="S" tag="span">
        {label}
      </Typography>
      <Typography type="BODY" size="S" tag="div">
        {value || "-"}
      </Typography>
    </div>
  );

  return (
    <>
      <div className={screen}>
        <PartnerConsentBanner flow={flow} />

        <div className={`${content} ${contentWide} scrollbar`}>
          <div className={preconfirmHeader}>
            <Typography type="HEADING" size={isMobile ? "M" : "L"} tag="h1">
              {preconfirmation.title}
            </Typography>
            <Typography type="BODY" size="S">
              {preconfirmation.subtitle}
            </Typography>
          </div>

          <section className={infoCard}>
            <Typography type="HEADING" size="M" tag="h2">
              {preconfirmation.accountTitle}
            </Typography>
            <div className={infoGrid}>
              {field(
                preconfirmation.cuit,
                // Stripped first: the backend may already send the dashes.
                cuit && asCuit(cuit.replace(/\D/g, "")),
              )}
              {field(preconfirmation.firstName, firstName)}
              {field(preconfirmation.lastName, lastName)}
              {field(preconfirmation.accountNumber, comitente)}
            </div>
          </section>
        </div>
      </div>

      <ButtonGroup
        container
        align="right"
        containerClassName={actionsBar}
        secondary={{
          text: preconfirmation.back,
          type: "button",
          testId: "consent-back",
          isDisabled: isSubmitting,
          onClick: onBack,
        }}
        primary={{
          text: preconfirmation.confirm,
          type: "button",
          testId: "consent-confirm",
          isDisabled: isSubmitting,
          isLoading: isSubmitting,
          onClick: onConfirm,
        }}
      />
    </>
  );
};

export default PartnerConsentPreconfirmation;
